import type { MessageAuthorRole, ChatMessage } from '../types/chat-message.js';
import type { MessageContentType } from '../design/component-inventory.js';
import {
  THREAD_MESSAGE_SOURCE_ID,
  type ThreadApiMessage,
  type ThreadApiResponse,
} from '../types/thread-api.js';
import { mapThreadMessageAttachment } from '../rich-media/map-from-thread.js';
import { areEquivalentMediaUrls } from '../rich-media/normalize-media-url.js';
import { isThreadApiMessageDisplayable } from './message-display.js';
import { sortMessagesByDate } from '../types/chat-message.js';

/** Roles that must not appear in the visitor-facing chat UI */
const HIDDEN_MESSAGE_ROLES = new Set([4]);

/** Backend message types hidden from widget (func calls / internal) */
const HIDDEN_MESSAGE_TYPES = new Set([6, 7]);

const HIDDEN_RAW_TYPES = new Set(['func_result', 'func_call']);

/**
 * Laravel `message_role` → widget author role.
 *
 * | role | Meaning        |
 * |------|----------------|
 * | 1    | bot / AI       |
 * | 2    | user (visitor) |
 * | 3    | manager        |
 * | 4    | system (hidden)|
 */
export function mapThreadMessageRole(
  messageRole: number,
): MessageAuthorRole | null {
  switch (messageRole) {
    case 1:
      return 'bot';
    case 2:
      return 'user';
    case 3:
      return 'manager';
    case 4:
      return 'system';
    default:
      return null;
  }
}

export function isThreadMessageVisible(message: ThreadApiMessage): boolean {
  if (HIDDEN_MESSAGE_ROLES.has(message.message_role)) return false;
  if (HIDDEN_MESSAGE_TYPES.has(message.type)) return false;
  if (HIDDEN_RAW_TYPES.has(message.raw_message?.type ?? '')) return false;

  const sourceId = message.source_id;
  if (sourceId === THREAD_MESSAGE_SOURCE_ID.INTERNAL) return false;
  if (sourceId === THREAD_MESSAGE_SOURCE_ID.WELCOME) return false;
  if (sourceId === THREAD_MESSAGE_SOURCE_ID.TYPING) return false;
  if (
    sourceId === THREAD_MESSAGE_SOURCE_ID.FUNC_CALL &&
    message.raw_message?.type === 'func_call'
  ) {
    return false;
  }

  return true;
}

function mapContentType(message: ThreadApiMessage): MessageContentType {
  const rawType = (message.raw_message?.type ?? '').toLowerCase();

  if (rawType === 'image_url' || message.type === 2) return 'image';
  if (message.type === 3) return 'video';
  if (message.type === 4) return 'file';
  if (message.type === 5) return 'voice';
  if (rawType === 'video_url' || rawType === 'video') return 'video';
  if (
    rawType === 'audio_url' ||
    rawType === 'audio' ||
    rawType === 'voice' ||
    rawType === 'voice_url'
  ) {
    return 'voice';
  }
  if (
    rawType === 'file' ||
    rawType === 'document' ||
    rawType === 'file_url' ||
    rawType === 'attachment'
  ) {
    return 'file';
  }
  if (rawType === 'text' || message.type === 1) return 'text';

  return 'text';
}

function resolveText(message: ThreadApiMessage, contentType: MessageContentType): string {
  const payload = message.raw_message?.payload?.trim();
  if (contentType === 'image') return payload ?? message.message;

  const body = message.message?.trim() ?? '';
  if (payload) return payload;
  if (body) return body;

  return '';
}

/** Why a thread row was dropped during mapping (for SYNC debug logs). */
export function explainHiddenThreadMessage(message: ThreadApiMessage): string | null {
  if (!isThreadMessageVisible(message)) {
    return `visibility filter (role=${message.message_role} type=${message.type} source=${message.source_id})`;
  }

  const role = mapThreadMessageRole(message.message_role);
  if (!role || role === 'system') {
    return `role filter (${message.message_role})`;
  }

  const contentType = mapContentType(message);
  const text = resolveText(message, contentType);
  const attachment = mapThreadMessageAttachment(message);

  if (!isThreadApiMessageDisplayable(message, text, attachment)) {
    return `not displayable (message="${(message.message ?? '').slice(0, 80)}" text="${text.slice(0, 80)}" rawType=${message.raw_message?.type ?? '∅'} payload="${(message.raw_message?.payload ?? '').slice(0, 80)}")`;
  }

  return null;
}

/** Map a single Laravel thread message to widget `ChatMessage`, or `null` if hidden. */
export function mapThreadMessage(message: ThreadApiMessage): ChatMessage | null {
  if (!isThreadMessageVisible(message)) return null;

  const role = mapThreadMessageRole(message.message_role);
  if (!role || role === 'system') return null;

  const contentType = mapContentType(message);
  const text = resolveText(message, contentType);
  const attachment = mapThreadMessageAttachment(message);
  const mediaUrl =
    attachment?.url ??
    (contentType === 'image' ? text : undefined);

  if (!isThreadApiMessageDisplayable(message, text, attachment)) {
    return null;
  }

  const chatMessage: ChatMessage = {
    id: String(message.id),
    role,
    text:
      attachment && (contentType === 'image' || attachment.kind !== 'file')
        ? text.trim() &&
          attachment.url &&
          !areEquivalentMediaUrls(text, attachment.url)
          ? text
          : ''
        : text,
    createdAt: message.created_at,
    contentType,
    mediaUrl: contentType === 'image' && !attachment ? text : mediaUrl,
    attachment,
  };

  if (role === 'user') {
    chatMessage.status = 'sent';
  }

  return chatMessage;
}

/** Map thread `messages[]` → sorted widget messages (oldest first). */
export function mapThreadMessages(messages: ThreadApiMessage[]): ChatMessage[] {
  const mapped = messages
    .map(mapThreadMessage)
    .filter((msg): msg is ChatMessage => msg !== null);

  return sortMessagesByDate(mapped);
}

/** Convenience: map full `GET /api/thread/{id}` response. */
export function mapThreadResponse(thread: ThreadApiResponse): ChatMessage[] {
  return mapThreadMessages(thread.messages);
}
